import { useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Send, CheckCircle2, Loader2, Phone } from "lucide-react";
import { api, errorMessage } from "../../lib/api";
import { trackEvent } from "../../lib/analytics";
import { COMPANY } from "../../data/site";

const PLACES = ["Maison & jardin", "Jardin avec piscine / bassin", "Gîte, camping, location", "Restaurant, hôtel, commerce", "Entreprise / copropriété", "Collectivité / mairie", "Événement en extérieur"];
const SURFACES = ["Moins de 300 m²", "300 à 800 m²", "800 m² à 2 000 m²", "Plus de 2 000 m²", "Je ne sais pas"];

const field = "w-full rounded-xl border border-moss/20 bg-white/80 px-4 py-3.5 text-[15px] text-ink placeholder:text-ink/45 outline-none transition-[border-color,box-shadow] focus:border-moss focus:shadow-[0_0_0_4px_rgba(26,77,46,.12)]";
const Label = ({ children, htmlFor }) => <label htmlFor={htmlFor} className="block text-xs font-bold uppercase tracking-[0.14em] text-sage mb-2">{children}</label>;

export const QuoteForm = ({ compact = false }) => {
  const { pathname } = useLocation();
  const [form, setForm] = useState({ name: "", phone: "", email: "", city: "", place_type: PLACES[0], surface: "", message: "", consent: false, website: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.consent) { setError("Merci d'accepter d'être recontacté pour recevoir votre devis."); return; }
    setStatus("loading");
    try {
      await api.post("/leads", { ...form, email: form.email || null, source_page: pathname });
      setStatus("done");
      trackEvent("generate_lead", { page_path: pathname, city: form.city });
    } catch (err) {
      setStatus("idle");
      setError(errorMessage(err));
    }
  };

  return (
    <div className="relative rounded-[28px] bg-cream border border-moss/15 p-6 sm:p-8 lg:p-10 shadow-[0_40px_80px_-40px_rgba(15,42,25,.45)]" data-testid="quote-form-card">
      <AnimatePresence initial={false}>
        {status === "done" ? (
          <motion.div key="ok" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="py-10 text-center" data-testid="quote-form-success">
            <CheckCircle2 className="w-14 h-14 mx-auto text-moss" />
            <h3 className="mt-5 text-3xl font-extrabold text-forest">Demande bien reçue !</h3>
            <p className="mt-3 text-ink/75 max-w-sm mx-auto">Nous vous rappelons rapidement pour organiser le diagnostic de votre extérieur. Urgent ? Appelez-nous directement.</p>
            <a href={COMPANY.phoneHref} className="btn btn-forest mt-6"><Phone className="w-4 h-4" />{COMPANY.phone}</a>
          </motion.div>
        ) : (
          <motion.form key="form" onSubmit={submit} className="space-y-4" data-testid="quote-form" noValidate={false}>
            {!compact && (
              <div className="mb-2">
                <p className="eyebrow text-sage">Devis gratuit & sans engagement</p>
                <h3 className="mt-3 text-2xl sm:text-3xl font-extrabold text-forest leading-tight">Décrivez-nous votre extérieur, on s'occupe des moustiques.</h3>
              </div>
            )}
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label htmlFor="q-name">Nom & prénom *</Label><input id="q-name" required minLength={2} value={form.name} onChange={set("name")} className={field} placeholder="Votre nom" data-testid="quote-name-input" autoComplete="name" /></div>
              <div><Label htmlFor="q-phone">Téléphone *</Label><input id="q-phone" required type="tel" minLength={8} value={form.phone} onChange={set("phone")} className={field} placeholder="06 00 00 00 00" data-testid="quote-phone-input" autoComplete="tel" /></div>
              <div><Label htmlFor="q-email">Email</Label><input id="q-email" type="email" value={form.email} onChange={set("email")} className={field} placeholder="vous@email.fr" data-testid="quote-email-input" autoComplete="email" /></div>
              <div><Label htmlFor="q-city">Commune *</Label><input id="q-city" required minLength={2} value={form.city} onChange={set("city")} className={field} placeholder="Ex : Mérignac" data-testid="quote-city-input" autoComplete="address-level2" /></div>
              <div><Label htmlFor="q-place">Type de lieu</Label>
                <select id="q-place" value={form.place_type} onChange={set("place_type")} className={field} data-testid="quote-place-select">{PLACES.map(p => <option key={p}>{p}</option>)}</select></div>
              <div><Label htmlFor="q-surface">Surface extérieure</Label>
                <select id="q-surface" value={form.surface} onChange={set("surface")} className={field} data-testid="quote-surface-select"><option value="">Choisir…</option>{SURFACES.map(s => <option key={s}>{s}</option>)}</select></div>
            </div>
            <div><Label htmlFor="q-msg">Votre situation</Label><textarea id="q-msg" rows={compact ? 3 : 4} value={form.message} onChange={set("message")} className={field} placeholder="Moustique tigre, piscine, bassin, haies, période d'intervention souhaitée…" data-testid="quote-message-input" /></div>
            <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={set("website")} className="hidden" aria-hidden="true" />
            <label className="flex items-start gap-3 text-[13px] text-ink/75 cursor-pointer">
              <input type="checkbox" checked={form.consent} onChange={set("consent")} className="mt-0.5 w-5 h-5 accent-[#1A4D2E] shrink-0" data-testid="quote-consent-checkbox" />
              <span>J'accepte que Termite Habitat 33 utilise ces informations pour me recontacter au sujet de ma demande de devis (voir notre politique de confidentialité).</span>
            </label>
            {error && <p className="text-sm font-semibold text-red-700" role="alert" data-testid="quote-form-error">{error}</p>}
            <button type="submit" disabled={status === "loading"} className="btn btn-forest w-full !py-4 text-base disabled:opacity-70" data-testid="quote-submit-button">
              {status === "loading" ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-4 h-4" />}
              Recevoir mon devis gratuit
            </button>
            <p className="text-center text-xs text-ink/60">Réponse rapide · Déplacement inclus · Bordeaux & toute la Gironde</p>
          </motion.form>
        )}
      </AnimatePresence>
    </div>
  );
};
